import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaRegHeart, FaHeart } from 'react-icons/fa6';
import { FiSearch, FiArrowLeft, FiTrash2 } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import useFavorites from '../../../configurations/useFavorites';
import ContainerPublic from '../../base/ContainerPublic';
import DoctorsCard from '../../controls/DoctorsCard';
import FabToggle from '../../base/FabToggle';
import Footer from '../../base/Footer';

import { IsMobileEnv } from '../../../configurations/initUrls';
import ConfirmModal from './ConfirmModal';

// doctor ka naam best-effort nikaalo (search filter ke liye)
const getName = (item) =>
    (item?.fullName ||
        item?.name ||
        `${item?.firstName ?? ''} ${item?.lastName ?? ''}`.trim() ||
        item?.userName ||
        '').toLowerCase();

export default function Favorites() {
    const { favorites, count, clearFavorites } = useFavorites();
    const [term, setTerm] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const filtered = useMemo(() => {
        const q = term.trim().toLowerCase();
        if (!q) return favorites;
        return favorites.filter((f) => getName(f).includes(q));
    }, [favorites, term]);

    const handleClearAll = () => {
        clearFavorites();
        setConfirmOpen(false);
    };

    return (
        <ContainerPublic>
            <div className="min-h-screen flex flex-col text-gray-800 bg-slate-100">

                {/* ---- Top bar ---- */}
                <div className="fixed w-full z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-soft py-2.5 px-3 sm:px-4 flex gap-2 justify-between items-center">
                    <div className="flex items-center gap-2 min-w-0">
                        <Link to="/" className="grid place-items-center h-9 w-9 rounded-full hover:bg-gray-100 transition text-gray-600">
                            <FiArrowLeft size={20} />
                        </Link>
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="grid place-items-center h-8 w-8 rounded-full bg-red-50 text-red-500 shrink-0">
                                <FaHeart size={15} />
                            </span>
                            <div className="min-w-0">
                                <h1 className="text-sm sm:text-base font-bold leading-tight truncate">My Doctors</h1>
                                <p className="text-[11px] text-gray-400 leading-tight">
                                    {count} saved {count === 1 ? 'doctor' : 'doctors'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {count > 0 && (
                        <button
                            type="button"
                            onClick={() => setConfirmOpen(true)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition shrink-0"
                        >
                            <FiTrash2 size={15} /> <span className="hidden sm:inline">Clear all</span>
                        </button>
                    )}
                </div>
                <div className="pt-[56px]" />

                {/* ---- Search (sirf jab kuch favorites ho) ---- */}
                {count > 0 && (
                    <div className="px-4 pt-4">
                        <div className="mx-auto w-full max-w-2xl relative">
                            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={term}
                                onChange={(e) => setTerm(e.target.value)}
                                placeholder="Search your saved doctors..."
                                className="text-box pl-10 pr-9"
                            />
                            {term && (
                                <button
                                    type="button"
                                    onClick={() => setTerm('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <IoClose />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* ---- List / Empty state ---- */}
                <div className="flex flex-col justify-between grow">
                    <section className="py-6 px-4 sm:px-6 md:px-20 bg-slate-100">
                        {count === 0 ? (
                            <EmptyState />
                        ) : filtered.length === 0 ? (
                            <div className="text-center py-16">
                                <p className="text-gray-400 font-medium">No doctor matches “{term}”.</p>
                            </div>
                        ) : (
                            <div className="mx-auto max-w-6xl grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filtered.map((item, i) => (
                                    <DoctorsCard key={i} item={item} />
                                ))}
                            </div>
                        )}
                    </section>

                    {!IsMobileEnv ? <section><Footer /></section> : <div className="p-10 bg-white" />}
                </div>
            </div>

            <FabToggle />

            {/* ---- Clear all confirmation ---- */}
            <ConfirmModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleClearAll}
                title="Remove all favorites?"
                message={`Ye aapke saare ${count} saved doctors hata denge. Ye action wapas nahi hoga.`}
                confirmText="Clear all"
                cancelText="Cancel"
                tone="danger"
                icon={<FiTrash2 size={26} />}
            />
        </ContainerPublic>
    );
}

function EmptyState() {
    return (
        <div className="mx-auto max-w-md text-center py-16 animate-fade-up">
            <div className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-red-50 text-red-300 mb-5">
                <FaRegHeart size={38} />
            </div>
            <h2 className="text-xl font-bold text-gray-700">No favorite doctors yet</h2>
            <p className="text-sm text-gray-400 mt-2 mb-6">
                Doctors par heart icon tap karke unhe yahan save karo. Ye list aapke device par
                permanently save rahegi.
            </p>
            <Link to="/" className="btn-blue inline-flex items-center justify-center gap-2 px-6">
                Browse doctors
            </Link>
        </div>
    );
}